import {
  Flashbar,
  SpaceBetween,
} from '@cloudscape-design/components';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useRef,
  useState,
} from 'react';

import parseScanCode, { ScanTypes } from '../utils/parseScanCode';
import QRCodeScanner from './qrCodeScanner';

/**
 * Camera scanner emitting parsed codes through onScan({ type, id }).
 * Codes whose type isn't in `accept` are rejected with an inline error.
 * accept/onScan are read through refs so the success callback keeps a
 * stable identity — otherwise the camera restarts on every re-render.
 */
export default function CameraScanner({
  id,
  accept,
  onScan,
}) {
  const [error, setError] = useState(null);
  const acceptRef = useRef(accept);
  acceptRef.current = accept;
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const onQRCodeSuccess = useCallback((qrCode) => {
    setError(null);
    const { type, id: scannedId, error: parseError } = parseScanCode(qrCode);
    if (parseError) {
      setError(parseError);
      return;
    }
    if (!acceptRef.current.includes(type)) {
      setError(`Scanned a ${type} code — this field accepts ${acceptRef.current.join(' or ')} codes.`);
      return;
    }
    onScanRef.current({ type, id: scannedId });
  }, []);

  return (
    <SpaceBetween size="m">
      {error ? (
        <Flashbar
          items={[{
            header: 'Error scanning QR code',
            content: error,
            type: 'error',
          }]}
        />
      ) : null}
      {/* html5-qrcode remembers the last-used camera by default. */}
      <QRCodeScanner
        id={id}
        qrCodeSuccessCallback={onQRCodeSuccess}
        qrbox={200}
      />
    </SpaceBetween>
  );
}

CameraScanner.propTypes = {
  id: PropTypes.string.isRequired,
  accept: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ScanTypes))).isRequired,
  onScan: PropTypes.func.isRequired,
};
